"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";

const NAV_NS = "factoryShowcase";

interface FacilityItem {
	id: number;
	titleKey: string;
	imageSrc: string;
}

interface SlugConfig {
	namespace: string;
	facilities: FacilityItem[];
}

const STAMPING_IMG_BASE = "/image/new-version/fabrication/metal-stamping/Engineered-for-Reliability";
const METAL_CASTING_IMG_BASE = "/image/new-version/casting/Metal-Casting/Manufacturer";
const THREE_D_PRINTING_IMG_BASE = "/image/new-version/more/3D-Printing/Manufacturer";
const ALUMINUM_EXTRUSION_IMG_BASE = "/image/new-version/more/AluminumExtrusion/quality-you-can-trust";
const SPRING_MANUFACTURING_IMG_BASE = "/image/new-version/more/SpringManufacturing/Factory";
const SURFACE_FINISHING_IMG_BASE = "/image/new-version/ValueAddedSolutions/surface-finishing";
const ON_DEMAND_MANUFACTURING_IMG_BASE = "/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing";
const ASSEMBLY_IMG_BASE = "/image/new-version/ValueAddedSolutions/assembly";
const INDUSTRIAL_AUTOMATION_IMG_BASE = "/image/new-version/ValueAddedSolutions/industrial-automation";

const STAMPING_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${STAMPING_IMG_BASE}/stamping-assembly-facility.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${STAMPING_IMG_BASE}/stamping-facility-in-dongguan.jpg`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${STAMPING_IMG_BASE}/stamping-facility-in-shenzhen.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${STAMPING_IMG_BASE}/tooling-center-in-dongguan.webp`,
	},
];

const METAL_CASTING_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${METAL_CASTING_IMG_BASE}/precision-casting-foundry-in-dongguan.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${METAL_CASTING_IMG_BASE}/high-pressure-die-casting-facility-in-shenzhen.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${METAL_CASTING_IMG_BASE}/investment-casting-center-in-shenzhen.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${METAL_CASTING_IMG_BASE}/heat-treatment-workshop-in-dongguan.webp`,
	},
];

const THREE_D_PRINTING_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${THREE_D_PRINTING_IMG_BASE}/professional-3d-printing-services-manufacturer-1.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${THREE_D_PRINTING_IMG_BASE}/professional-3d-printing-services-manufacturer-2.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${THREE_D_PRINTING_IMG_BASE}/professional-3d-printing-services-manufacturer-3.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${THREE_D_PRINTING_IMG_BASE}/professional-3d-printing-services-manufacturer-4.webp`,
	},
];

const ALUMINUM_EXTRUSION_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${ALUMINUM_EXTRUSION_IMG_BASE}/quality-you-can-trust-1.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${ALUMINUM_EXTRUSION_IMG_BASE}/quality-you-can-trust-2.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${ALUMINUM_EXTRUSION_IMG_BASE}/quality-you-can-trust-3.jpg`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${ALUMINUM_EXTRUSION_IMG_BASE}/quality-you-can-trust-4.jpg`,
	},
];

const SPRING_MANUFACTURING_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${SPRING_MANUFACTURING_IMG_BASE}/spring-machining-workshop-1.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${SPRING_MANUFACTURING_IMG_BASE}/spring-machining-workshop-2.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${SPRING_MANUFACTURING_IMG_BASE}/spring-machining-workshop-3.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${SPRING_MANUFACTURING_IMG_BASE}/spring-machining-workshop-4.webp`,
	},
];

const SURFACE_FINISHING_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${SURFACE_FINISHING_IMG_BASE}/professional-custom-surface-finishing-services.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${SURFACE_FINISHING_IMG_BASE}/surface-finishing-services-with-js-precision.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${SURFACE_FINISHING_IMG_BASE}/perfect-your-projects-surface-finish.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${SURFACE_FINISHING_IMG_BASE}/what-is-surface-finishing.webp`,
	},
];

const ON_DEMAND_MANUFACTURING_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${ON_DEMAND_MANUFACTURING_IMG_BASE}/js-precision-on-demand-manufacturing.jpg`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${ON_DEMAND_MANUFACTURING_IMG_BASE}/HowitWorks/11-js-upload-design-file.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${ON_DEMAND_MANUFACTURING_IMG_BASE}/HowitWorks/12-js-get-quote-with-dfm.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${ON_DEMAND_MANUFACTURING_IMG_BASE}/HowitWorks/13-js-delivery.webp`,
	},
];

const ASSEMBLY_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${ASSEMBLY_IMG_BASE}/assembly-methods-for-parts/mechanical-assembly.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${ASSEMBLY_IMG_BASE}/assembly-methods-for-parts/manual-assembly.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${ASSEMBLY_IMG_BASE}/assembly-methods-for-parts/adhesive-assembly.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${ASSEMBLY_IMG_BASE}/assembly-methods-for-parts/molding-assembly.webp`,
	},
];

const INDUSTRIAL_AUTOMATION_FACILITIES: FacilityItem[] = [
	{
		id: 1,
		titleKey: "factoryShowcaseCard1Title",
		imageSrc: `${INDUSTRIAL_AUTOMATION_IMG_BASE}/full-cycle-rapid-manufacturing-solutions-for-automation-industry.webp`,
	},
	{
		id: 2,
		titleKey: "factoryShowcaseCard2Title",
		imageSrc: `${INDUSTRIAL_AUTOMATION_IMG_BASE}/full-cycle-rapid-manufacturing-partner-for-automation-projects.webp`,
	},
	{
		id: 3,
		titleKey: "factoryShowcaseCard3Title",
		imageSrc: `${INDUSTRIAL_AUTOMATION_IMG_BASE}/expert-team-support.webp`,
	},
	{
		id: 4,
		titleKey: "factoryShowcaseCard4Title",
		imageSrc: `${INDUSTRIAL_AUTOMATION_IMG_BASE}/CaseStudy/precision-manufacturing-flexible-adaptation-for-new-energy-vehicle-connector-components.webp`,
	},
];

const SLUG_CONFIG: Record<string, SlugConfig> = {
	stamping: {
		namespace: "new-stamping",
		facilities: STAMPING_FACILITIES,
	},
	"metal-casting": {
		namespace: "new-metal-casting",
		facilities: METAL_CASTING_FACILITIES,
	},
	"sand-casting": {
		namespace: "new-sand-casting",
		facilities: METAL_CASTING_FACILITIES,
	},
	"die-casting": {
		namespace: "new-die-casting",
		facilities: METAL_CASTING_FACILITIES,
	},
	"3d-printing": {
		namespace: "new-3d-printing",
		facilities: THREE_D_PRINTING_FACILITIES,
	},
	"aluminum-extrusion": {
		namespace: "new-aluminum-extrusion",
		facilities: ALUMINUM_EXTRUSION_FACILITIES,
	},
	"spring-manufacturing": {
		namespace: "new-spring-manufacturing",
		facilities: SPRING_MANUFACTURING_FACILITIES,
	},
	"surface-finishing": {
		namespace: "new-surface-finishing",
		facilities: SURFACE_FINISHING_FACILITIES,
	},
	"on-demand-manufacturing": {
		namespace: "new-on-demand-manufacturing",
		facilities: ON_DEMAND_MANUFACTURING_FACILITIES,
	},
	assembly: {
		namespace: "new-assembly",
		facilities: ASSEMBLY_FACILITIES,
	},
	"industrial-automation": {
		namespace: "new-industrial-automation",
		facilities: INDUSTRIAL_AUTOMATION_FACILITIES,
	},
};

const DEFAULT_SLUG = "stamping";

interface FacilitySlideProps {
	item: FacilityItem;
	title: string;
	showTitle?: boolean;
}

function FacilitySlide({ item, title, showTitle = true }: FacilitySlideProps) {
	return (
		<article className="relative h-[340px] w-full overflow-hidden sm:h-[390px] sm:max-w-[370px]">
			<Image
				src={item.imageSrc}
				alt={title}
				fill
				className="object-cover"
				sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
			/>

			{showTitle && (
				<div className="absolute bottom-0 left-0 right-0 mx-auto w-[95%] bg-[#2a2a2a] px-4 py-3 sm:py-4">
					<div className="flex items-center justify-between">
						<h3 className="text-white sm:text-base">{title}</h3>
					</div>
				</div>
			)}
		</article>
	);
}

interface FactoryShowcaseProps {
	slug?: string;
}

export default function FactoryShowcase({ slug }: FactoryShowcaseProps) {
	const config = SLUG_CONFIG[slug ?? DEFAULT_SLUG] ?? SLUG_CONFIG[DEFAULT_SLUG];
	const t = useTranslations(config.namespace);
	const tNav = useTranslations(NAV_NS);
	const showCardTitle = slug !== "3d-printing";

	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);
	const showNavigation = config.facilities.length > 4;

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative">
					<Swiper
						modules={[Navigation]}
						spaceBetween={16}
						slidesPerView={1}
						breakpoints={{
							640: {
								slidesPerView: 2,
								spaceBetween: 20,
							},
							1024: {
								slidesPerView: 3,
								spaceBetween: 24,
							},
							1280: {
								slidesPerView: 4,
								spaceBetween: 24,
							},
						}}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
						}}
						onSlideChange={handleSlideChange}
						className="overflow-hidden"
					>
						{config.facilities.map((item) => {
							const title = t(item.titleKey);
							return (
								<SwiperSlide key={item.id} className="flex justify-center">
									<FacilitySlide item={item} title={title} showTitle={showCardTitle} />
								</SwiperSlide>
							);
						})}
					</Swiper>

					{showNavigation && (
						<div className="mt-6 flex items-center justify-end gap-6 sm:mt-8 2xl:mr-32">
							<button
								type="button"
								onClick={() => swiperRef.current?.slidePrev()}
								disabled={isBeginning}
								className={`transition-colors duration-200 ${
									isBeginning ? "cursor-not-allowed text-gray-300" : "text-themePrimary400 hover:text-mainColor"
								}`}
								aria-label={tNav("prevSlide")}
							>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M19 12H5M5 12L12 19M5 12L12 5"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>

							<button
								type="button"
								onClick={() => swiperRef.current?.slideNext()}
								disabled={isEnd}
								className={`transition-colors duration-200 ${
									isEnd ? "cursor-not-allowed text-gray-300" : "text-themePrimary400 hover:text-mainColor"
								}`}
								aria-label={tNav("nextSlide")}
							>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M5 12H19M19 12L12 5M19 12L12 19"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
